import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useMonthOptions } from '@/hooks/useMonthOptions';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import {
  Calendar,
  Clock,
  X,
  Loader2,
  CircleCheck,
  Timer,
  Ban,
  Building2,
  Plus,
  Pencil,
  Trash2,
  Stethoscope,
  BookOpen,
  ChevronDown,
  Copy,
  FileText,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useMyReportsByMonth, useReportSummary, useDeleteReport } from '../hooks';
import { useQueryParams } from '@/hooks/useQueryParams';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { QuickReportModal } from '../components/QuickReportModal';
import { DetailModal, ConfirmModal } from '@/components/modal';
import { localizedName } from '@/utils/localized';
import { useHospitals } from '@/features/hospitals';
import {
  staggerContainer,
  staggerItem,
  useAnimatedNumber,
  triggerHapticFeedback,
} from '../../admin/utils/adminAnimations';
import { ADMIN_COLORS, STATUS_BADGE } from '@/constants/theme';
import type { Report } from '../types';

// ─── Design tokens (sourced from shared theme) ─────────────────
const C = {
  // Primary
  primary: ADMIN_COLORS.primary,
  primarySoft: ADMIN_COLORS.primarySoft,
  primaryText: ADMIN_COLORS.primaryText,
  // Forest
  forest: ADMIN_COLORS.forest,
  forestSoft: ADMIN_COLORS.forestSoft,
  forestText: ADMIN_COLORS.forestText,
  // Leaf
  leaf: ADMIN_COLORS.leaf,
  leafSoft: ADMIN_COLORS.leafSoft,
  leafText: ADMIN_COLORS.leafText,
  // Text
  textPrimary: ADMIN_COLORS.navy,
  textSecondary: ADMIN_COLORS.textSecondary,
  textTertiary: ADMIN_COLORS.textTertiary,
  // Surfaces
  surface: ADMIN_COLORS.surface,
  bg: ADMIN_COLORS.ice,
  border: ADMIN_COLORS.border,
  borderMedium: ADMIN_COLORS.borderMedium,
  // Shadows
  shadow: ADMIN_COLORS.shadow,
  shadowMd: ADMIN_COLORS.shadowMd,
  shadowLg: ADMIN_COLORS.shadowLg,
  // Semantic
  success: ADMIN_COLORS.success,
  successBg: ADMIN_COLORS.successBg,
  successText: ADMIN_COLORS.successText,
  warning: ADMIN_COLORS.warning,
  warningBg: ADMIN_COLORS.warningBg,
  warningText: ADMIN_COLORS.warningText,
  danger: ADMIN_COLORS.signal,
  dangerBg: ADMIN_COLORS.dangerBg,
  dangerText: ADMIN_COLORS.dangerText,
  infoBg: ADMIN_COLORS.infoBg,
  infoText: ADMIN_COLORS.infoText,
} as const;

// ─── Status badge config (colors from shared STATUS_BADGE) ─────
const STATUS_CONFIG: Record<string, { bg: string; color: string; icon: React.ReactNode; dot: string }> = {
  DRAFT: { bg: STATUS_BADGE.DRAFT.bg, color: STATUS_BADGE.DRAFT.color, icon: <Timer size={12} strokeWidth={2.5} />, dot: STATUS_BADGE.DRAFT.dot },
  SUBMITTED: { bg: STATUS_BADGE.SUBMITTED.bg, color: STATUS_BADGE.SUBMITTED.color, icon: <Timer size={12} strokeWidth={2.5} />, dot: STATUS_BADGE.SUBMITTED.dot },
  APPROVED: { bg: STATUS_BADGE.APPROVED.bg, color: STATUS_BADGE.APPROVED.color, icon: <CircleCheck size={12} strokeWidth={2.5} />, dot: STATUS_BADGE.APPROVED.dot },
  REJECTED: { bg: STATUS_BADGE.REJECTED.bg, color: STATUS_BADGE.REJECTED.color, icon: <Ban size={12} strokeWidth={2.5} />, dot: STATUS_BADGE.REJECTED.dot },
  PAID: { bg: STATUS_BADGE.PAID.bg, color: STATUS_BADGE.PAID.color, icon: <CircleCheck size={12} strokeWidth={2.5} />, dot: STATUS_BADGE.PAID.dot },
};

// ─── Animated value ─────────────────────────────────────────────
function AnimatedValue({ value }: { value: number }) {
  const d = useAnimatedNumber(value);
  return <>{d.toLocaleString()}</>;
}

// ─── Month helpers ──────────────────────────────────────────────
function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(month: string, lang: string): string {
  const [year, m] = month.split('-');
  const d = new Date(Number(year), Number(m) - 1, 1);
  return d.toLocaleDateString(lang === 'he' ? 'he-IL' : 'en-US', {
    month: 'long',
    year: 'numeric',
  });
}


function getDaysInMonth(month: string): Date[] {
  const [y, m] = month.split('-').map(Number);
  const days: Date[] = [];
  const d = new Date(y, m - 1, 1);
  while (d.getMonth() === m - 1) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isWeekend(d: Date): boolean {
  const day = d.getDay();
  return day === 5 || day === 6; // Friday or Saturday
}

function isToday(d: Date): boolean {
  const now = new Date();
  return d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
}

function formatDayLabel(d: Date, lang: string): string {
  const dayStr = String(d.getDate()).padStart(2, '0');
  const monthStr = String(d.getMonth() + 1).padStart(2, '0');
  const dayName = d.toLocaleDateString(lang === 'he' ? 'he-IL' : 'en-US', { weekday: 'short' });
  return `${dayStr}/${monthStr} ${dayName}`;
}

// ─── Summary Card ──────────────────────────────────────────────
function SummaryCard({ label, value, icon, isCurrency }: { label: string; value: number; icon: React.ReactNode; isCurrency?: boolean }) {
  return (
    <div
      className="p-4 rounded-xl bg-white/80"
      style={{
        border: '1px solid #CED0D4',
        boxShadow: C.shadow,
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span style={{ color: C.textPrimary + '80' }}>{icon}</span>
        <p className="text-sm font-bold text-slate-500 uppercase">{label}</p>
      </div>
      <p className="text-2xl font-bold text-neutral-800">
        {isCurrency ? <><AnimatedValue value={value} /> &#8362;</> : <AnimatedValue value={value} />}
      </p>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────
export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  // URL-synced state
  const [params, setParams] = useQueryParams({
    month: { default: getCurrentMonth() },
    hospital: { default: '' },
    status: { default: '' },
    report: { default: '' },
    action: { default: '' },
  });

  const selectedMonth = params.month;
  const hospitalFilter = params.hospital;
  const statusFilter = params.status;
  const isQuickReportOpen = params.action === 'new' || params.action === 'edit';

  // Transient state (NOT URL-synced)
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null);
  const [newReportDate, setNewReportDate] = useState<string | undefined>();
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [showOnlyReported, setShowOnlyReported] = useState(!!statusFilter);
  useEffect(() => { setShowOnlyReported(!!statusFilter); }, [statusFilter]);

  // Hospitals for filter dropdown
  const { data: hospitalsData } = useHospitals();

  const todayRef = useRef<HTMLTableRowElement>(null);

  // Data
  const { data: reports, isLoading } = useMyReportsByMonth(selectedMonth);
  const { data: summary } = useReportSummary(selectedMonth);
  const deleteMutation = useDeleteReport();

  const totalReports = summary?.totalReports ?? 0;

  const currentMonth = getCurrentMonth();
  const isCurrentMonth = selectedMonth === currentMonth;

  // Month options for dropdown
  const locale = lang === 'he' ? 'he-IL' : 'en-US';
  const monthOptions = useMonthOptions(locale);

  // Calendar days
  const calendarDays = useMemo(() => getDaysInMonth(selectedMonth), [selectedMonth]);

  const filteredReports = useMemo(() => {
    let list = reports ?? [];
    if (hospitalFilter) list = list.filter(r => r.hospitalId === hospitalFilter);
    if (statusFilter) list = list.filter(r => r.status === statusFilter);
    return list;
  }, [reports, hospitalFilter, statusFilter]);

  // Derived from URL params — full Report objects
  const detailReport = useMemo(
    () => (params.report ? (filteredReports.find(r => r.id === params.report) ?? null) : null),
    [filteredReports, params.report],
  );

  const editingReport = useMemo<Report | undefined>(
    () =>
      params.action === 'edit' && params.report
        ? ((reports ?? []).find(r => r.id === params.report))
        : undefined,
    [reports, params.action, params.report],
  );

  const reportsByDate = useMemo(() => {
    const map = new Map<string, Report[]>();
    for (const r of filteredReports) {
      const dateKey = r.date.slice(0, 10);
      const existing = map.get(dateKey) ?? [];
      existing.push(r);
      map.set(dateKey, existing);
    }
    return map;
  }, [filteredReports]);


  // Auto-scroll to today — only within the table container, not the whole page.
  // Uses a slight delay so the layout is settled before measuring.
  const tableContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isLoading && todayRef.current && tableContainerRef.current) {
      const timer = setTimeout(() => {
        const container = tableContainerRef.current;
        const row = todayRef.current;
        if (!container || !row) return;
        // Scroll the table container so today's row is visible near the top
        const rowTop = row.offsetTop - container.offsetTop;
        container.scrollTo({ top: Math.max(0, rowTop - 60), behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, selectedMonth]);

  // Delete handlers
  const handleDeleteReport = useCallback((report: Report) => {
    setReportToDelete(report);
    setShowDeleteModal(true);
  }, []);

  const confirmDeletion = useCallback(async () => {
    if (!reportToDelete) return;
    setShowDeleteModal(false);
    setDeletingId(reportToDelete.id);
    try {
      await deleteMutation.mutateAsync(reportToDelete.id);
      toast.success(t('common.deleted'));
      setParams({ report: '' });
    } catch {
      toast.error(t('common.error'));
    } finally {
      setDeletingId(null);
      setReportToDelete(null);
    }
  }, [reportToDelete, deleteMutation, t, setParams]);

  const cancelDeletion = useCallback(() => {
    setShowDeleteModal(false);
    setReportToDelete(null);
  }, []);

  const closeDetailModal = useCallback(() => setParams({ report: '' }), [setParams]);

  // Row click handlers
  const handleEmptyDayClick = useCallback((dateStr: string) => {
    setNewReportDate(dateStr);
    setParams({ action: 'new', report: '' });
    triggerHapticFeedback();
  }, [setParams]);

  const handleSingleReportClick = useCallback((report: Report) => {
    setParams({ report: report.id });
    triggerHapticFeedback();
  }, [setParams]);

  const handleMultiReportToggle = useCallback((dateStr: string) => {
    setExpandedDate((prev) => (prev === dateStr ? null : dateStr));
    triggerHapticFeedback();
  }, []);

  const columnCount = 3;

  // ─── RENDER ──────────────────────────────────────────────────
  return (
    <>
      <motion.div
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.4 } } }}
        initial="hidden"
        animate="visible"
        className="pb-32 md:mx-auto md:max-w-[860px] md:px-6"
      >
       <div className="space-y-2 md:space-y-6">
        {/* Header — month title + New Report button */}
        <div className="px-4 pb-3 pt-6 md:px-0 md:pt-4" style={{ backgroundColor: 'var(--user-page-bg)' }}>
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-extrabold text-slate-900 md:text-2xl">
              {formatMonthLabel(selectedMonth, lang)}
            </h1>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => { setNewReportDate(undefined); setParams({ action: 'new', report: '' }); }}
              className="flex items-center gap-2 rounded-full font-semibold text-white transition-all"
              style={{
                backgroundColor: C.primary,
                boxShadow: '0 1px 3px color-mix(in srgb, var(--color-primary-500) 30%, transparent)',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                padding: isMobile ? '0.5rem 1rem' : '0.6rem 1.25rem',
              }}
            >
              <Plus size={16} strokeWidth={3} />
              <span>{t('reports.newReport')}</span>
            </motion.button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 md:px-0">
          <motion.div variants={staggerItem} className="flex flex-wrap items-center gap-2 md:gap-3">
            <div className="relative flex-1 min-w-[140px]">
              <select
                value={selectedMonth}
                onChange={(e) => { setParams({ month: e.target.value }); setExpandedDate(null); }}
                className="w-full appearance-none rounded-xl bg-white/80 backdrop-blur-sm ps-3 pe-8 py-2.5 text-sm font-medium text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 cursor-pointer"
                style={{ border: '1px solid #CED0D4' }}
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown size={14} strokeWidth={3} className="pointer-events-none absolute top-1/2 end-2.5 -translate-y-1/2 text-slate-500" />
            </div>

            <div className="relative flex-1 min-w-[140px]">
              <select
                value={statusFilter}
                onChange={(e) => setParams({ status: e.target.value })}
                className="w-full appearance-none rounded-xl bg-white/80 backdrop-blur-sm ps-3 pe-8 py-2.5 text-sm font-medium text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 cursor-pointer"
                style={{ border: '1px solid #CED0D4' }}
              >
                <option value="">{t('common.all')} {t('admin.statusCol')}</option>
                <option value="SUBMITTED">{t('status.submitted')}</option>
                <option value="APPROVED">{t('status.approved')}</option>
                <option value="REJECTED">{t('status.rejected')}</option>
              </select>
              <ChevronDown size={14} strokeWidth={3} className="pointer-events-none absolute top-1/2 end-2.5 -translate-y-1/2 text-slate-500" />
            </div>

            <div className="relative flex-1 min-w-[140px]">
              <select
                value={hospitalFilter}
                onChange={(e) => setParams({ hospital: e.target.value })}
                className="w-full appearance-none rounded-xl bg-white/80 backdrop-blur-sm ps-3 pe-8 py-2.5 text-sm font-medium text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 cursor-pointer"
                style={{ border: '1px solid #CED0D4' }}
              >
                <option value="">{t('common.all')} {t('hospitals.title')}</option>
                {(hospitalsData?.hospitals ?? []).map((h: { id: string; name: string; nameEn?: string }) => (
                  <option key={h.id} value={h.id}>
                    {localizedName(h, lang)}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} strokeWidth={3} className="pointer-events-none absolute top-1/2 end-2.5 -translate-y-1/2 text-slate-500" />
            </div>
          </motion.div>
        </div>

        {/* Show only reported days toggle */}
        <div className="px-4 md:px-0">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOnlyReported}
              onChange={(e) => setShowOnlyReported(e.target.checked)}
              className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
            />
            <span className="text-sm font-medium text-neutral-600">{t('myReports.showOnlyReported')}</span>
          </label>
        </div>

        {/* Stats Grid — Top (matching payroll layout) */}
        <div className="px-4 md:px-0">
          <motion.div variants={staggerItem} className="grid grid-cols-2 gap-2 md:gap-3">
            <SummaryCard label={t('myReports.totalReports')} value={totalReports} icon={<FileText size={16} strokeWidth={2} />} />
          </motion.div>
        </div>

        {/* Calendar Table */}
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center py-20"
            >
              <Loader2 size={24} strokeWidth={3} className="animate-spin" style={{ color: C.textTertiary }} />
            </motion.div>
          ) : (
            <motion.div
              key="content"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0 }}
            >
              <div
                ref={tableContainerRef}
                className="overflow-x-auto md:rounded-3xl md:glass-surface"
                style={{
                  backgroundColor: isMobile ? '#FFFFFF' : C.surface,
                  border: isMobile ? 'none' : '1px solid #CED0D4',
                  boxShadow: isMobile ? 'none' : C.shadow,
                }}
              >
                <table className="w-full text-[15px]">
                  <thead>
                    <tr className="border-b-[2px] text-slate-600" style={{ borderColor: '#CED0D4', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                      <th className="ps-6 pe-4 py-2.5 text-start font-semibold text-sm">
                        {t('reports.date')}
                      </th>
                      <th className="px-4 py-2.5 text-center font-semibold text-sm">
                        {t('reports.myReports')}
                      </th>
                      <th className="ps-4 pe-6 py-2.5 text-end font-semibold text-sm">
                        {t('admin.totalCol')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {calendarDays.filter((day) => !showOnlyReported || (reportsByDate.get(toDateStr(day))?.length ?? 0) > 0).map((day, idx) => {
                      const dateStr = toDateStr(day);
                      const dayReports = reportsByDate.get(dateStr) ?? [];
                      const weekend = isWeekend(day);
                      const today = isToday(day);
                      const isExpanded = expandedDate === dateStr;
                      const hasReports = dayReports.length > 0;
                      const isEmpty = dayReports.length === 0;

                      return (
                        <CalendarRows
                          key={dateStr}
                          day={day}
                          dateStr={dateStr}
                          dayReports={dayReports}
                          idx={idx}
                          today={today}
                          weekend={weekend}
                          isEmpty={isEmpty}
                          hasReports={hasReports}
                          isExpanded={isExpanded}
                          canReport={isCurrentMonth}
                          isMobile={isMobile}
                          columnCount={columnCount}
                          lang={lang}
                          t={t}
                          todayRef={today ? todayRef : undefined}
                          onEmptyClick={handleEmptyDayClick}
                          onToggle={handleMultiReportToggle}
                          onSubRowClick={handleSingleReportClick}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
       </div>

        {/* Bottom fade — mobile only */}
        <div
          className="pointer-events-none fixed inset-x-0 z-10 md:hidden"
          style={{ bottom: 0, height: 50, background: 'linear-gradient(to top, #F0F2F5, transparent)' }}
          aria-hidden="true"
        />
      </motion.div>

      {/* Detail Modal — uses shared DetailModal (BaseModal under the hood) */}
      <DetailModal
        isOpen={!!detailReport}
        onClose={closeDetailModal}
        maxWidth="max-w-lg"
        ariaLabel={t('myReports.detail.title')}
      >
        {detailReport && renderDetailContent(detailReport)}
      </DetailModal>

      {/* Delete Confirmation Modal — uses shared ConfirmModal (BaseModal under the hood) */}
      <ConfirmModal
        isOpen={showDeleteModal && !!reportToDelete}
        onClose={cancelDeletion}
        onConfirm={() => { confirmDeletion(); triggerHapticFeedback([50, 30, 50]); }}
        title={t('myReports.actions.confirmDeleteTitle')}
        message={t('myReports.actions.confirmDeleteMessage')}
        variant="danger"
        confirmLabel={t('myReports.actions.yesDelete')}
        cancelLabel={t('myReports.actions.keepReport')}
      />

      {/* QuickReport Modal */}
      <QuickReportModal
        isOpen={isQuickReportOpen}
        onClose={() => { setParams({ action: '' }); setNewReportDate(undefined); }}
        onSuccess={() => { /* queries auto-invalidated */ }}
        existingReport={editingReport}
        defaultDate={newReportDate}
      />
    </>
  );

  // Detail content — shared between desktop card / mobile drawer
  function renderDetailContent(report: Report) {
    const canEdit = report.status !== 'APPROVED';
    const canDelete = report.status === 'DRAFT';
    const isTreatment = report.reportTypeCode === 'TREATMENT';

    return (
      <>
        {/* Sticky Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-5 py-4"
          style={{
            backgroundColor: C.surface,
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div className="flex items-center gap-2.5">
            {isTreatment ? (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: C.primarySoft }}>
                <Stethoscope size={16} strokeWidth={2} style={{ color: C.primary }} />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: C.leafSoft }}>
                <BookOpen size={16} strokeWidth={2} style={{ color: C.leaf }} />
              </div>
            )}
            <h3 className="font-semibold text-sm" style={{ color: C.textPrimary }}>{t('myReports.detail.title')}</h3>
          </div>
          <button
            onClick={closeDetailModal}
            className="rounded-xl p-2 hover:bg-white/10 transition-colors"
          >
            <X size={16} strokeWidth={3} style={{ color: C.textSecondary }} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Status */}
          {(() => {
            const isResubmitted = report.status === 'SUBMITTED' && !!report.rejectionReason;
            const statusKey = isResubmitted ? 'SUBMITTED' : report.status;
            const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.DRAFT;
            return (
              <DetailRow label={t('status.label')}>
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: cfg.bg, color: cfg.color }}
                >
                  {cfg.icon}
                  {isResubmitted ? t('status.resubmitted') : t(`status.${report.status.toLowerCase()}`)}
                </span>
              </DetailRow>
            );
          })()}

          {/* Report type */}
          <DetailRow
            label={t('reports.reportType')}
            icon={isTreatment ? <Stethoscope size={16} strokeWidth={2} /> : <BookOpen size={16} strokeWidth={2} />}
          >
            {isTreatment ? t('reports.treatment') : t('reports.teaching')}
          </DetailRow>

          {/* Date */}
          <DetailRow label={t('reports.date')} icon={<Calendar size={16} strokeWidth={2} />}>
            {format(new Date(report.date), 'dd/MM/yyyy')}
          </DetailRow>

          {/* Hospital */}
          {report.hospital && (
            <DetailRow label={t('reports.hospital')} icon={<Building2 size={16} strokeWidth={2} />}>
              {localizedName(report.hospital, lang)}
            </DetailRow>
          )}

          {/* Treatment type + helmet */}
          {isTreatment && report.treatmentType && (
            <DetailRow label={t('reports.treatmentType')}>
              {t(`treatments.${report.treatmentType}`, report.treatmentType)}
              {report.helmetType ? ` (${report.helmetType})` : ''}
            </DetailRow>
          )}

          {/* Quantity */}
          {isTreatment && (
            <DetailRow label={t('quickReport.quantity')}>
              {report.quantity}
            </DetailRow>
          )}

          {/* Training hours */}
          {!isTreatment && report.hours != null && (
            <DetailRow label={t('teaching.hours')}>
              {report.hours}
            </DetailRow>
          )}

          {/* Time */}
          {report.startTime && report.endTime && (
            <DetailRow label={t('reports.time')} icon={<Clock size={16} strokeWidth={2} />}>
              {report.startTime} - {report.endTime}
            </DetailRow>
          )}

          {/* Description (training topic) */}
          {!isTreatment && report.description && (
            <div className="pt-3 mt-1" style={{ borderTop: `1px solid ${C.border}` }}>
              <p className="text-xs mb-1.5" style={{ color: C.textTertiary }}>{t('teaching.topic')}</p>
              <p className="text-sm leading-relaxed" style={{ color: C.textSecondary }}>{report.description}</p>
            </div>
          )}

          {/* Notes */}
          {report.notes && (
            <div className="pt-3 mt-1" style={{ borderTop: `1px solid ${C.border}` }}>
              <p className="text-xs mb-1.5" style={{ color: C.textTertiary }}>{t('reports.notes')}</p>
              <p className="text-sm leading-relaxed" style={{ color: C.textSecondary }}>{report.notes}</p>
            </div>
          )}

          {/* Rejection reason — currently rejected */}
          {report.status === 'REJECTED' && report.rejectionReason && (
            <div className="rounded-xl p-4 space-y-3 text-center" style={{ backgroundColor: C.dangerBg, border: `1px solid ${C.danger}25` }}>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.danger }}>
                {t('reports.rejectionReason')}
              </p>
              <p className="text-base font-medium italic leading-relaxed" style={{ color: C.dangerText }}>
                &ldquo;{report.rejectionReason}&rdquo;
              </p>
              <p className="text-xs" style={{ color: C.dangerText + '99' }}>
                {t('reports.rejectedAt')}: {format(new Date(report.updatedAt), 'dd/MM/yyyy HH:mm')}
              </p>
            </div>
          )}

          {/* Resubmission info — was rejected, now resubmitted */}
          {report.status === 'SUBMITTED' && report.rejectionReason && (
            <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: C.warningBg, border: `1px solid ${C.warning}30` }}>
              <p className="text-xs font-semibold uppercase tracking-wide text-center" style={{ color: C.warningText }}>
                {t('reports.previouslyRejected')}
              </p>
              <p className="text-sm italic text-center leading-relaxed" style={{ color: C.warningText }}>
                &ldquo;{report.rejectionReason}&rdquo;
              </p>
              <div className="pt-2 space-y-1" style={{ borderTop: `1px solid ${C.warning}20` }}>
                <p className="text-xs text-center" style={{ color: C.warningText + 'cc' }}>
                  {t('reports.correctedAndResubmitted')}
                </p>
                <p className="text-xs text-center" style={{ color: C.warningText + '99' }}>
                  {t('reports.resubmittedAt')}: {format(new Date(report.updatedAt), 'dd/MM/yyyy HH:mm')}
                </p>
              </div>
            </div>
          )}

          {/* Quantity summary */}
          <div className="pt-3 mt-1 flex justify-between items-center" style={{ borderTop: `1px solid ${C.border}` }}>
            <span className="font-medium text-sm" style={{ color: C.textPrimary }}>{t('reports.quantity')}</span>
            <span className="text-xl font-semibold" style={{ color: C.primary }}>
              {report.quantity || 1}
            </span>
          </div>
        </div>

        {/* Sticky Footer — Actions */}
        {(canEdit || canDelete) && (
          <div
            className="sticky bottom-0 flex gap-3 px-5 py-4"
            style={{
              backgroundColor: C.surface,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            {canEdit && (
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setNewReportDate(undefined);
                  setParams({ action: 'edit', report: report.id });
                }}
                className="flex-1 flex items-center justify-center gap-2 font-medium py-2.5 px-4 rounded-xl text-sm transition-colors"
                style={{
                  backgroundColor: C.bg,
                  border: `1px solid ${C.borderMedium}`,
                  color: C.textPrimary,
                }}
              >
                <Pencil size={16} strokeWidth={2} />
                {t('myReports.detail.editReport')}
              </motion.button>
            )}
            {canDelete && (
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleDeleteReport(report)}
                disabled={deletingId === report.id}
                className="flex-1 flex items-center justify-center gap-2 font-medium py-2.5 px-4 rounded-xl text-sm text-white disabled:opacity-50 transition-colors"
                style={{
                  backgroundColor: C.danger,
                  boxShadow: '0 1px 3px rgba(239,68,68,0.3)',
                }}
              >
                {deletingId === report.id ? (
                  <Loader2 size={16} strokeWidth={3} className="animate-spin" />
                ) : (
                  <Trash2 size={16} strokeWidth={2} />
                )}
                {deletingId === report.id ? t('myReports.detail.deleting') : t('myReports.detail.deleteReport')}
              </motion.button>
            )}
          </div>
        )}
      </>
    );
  }
}

// ─── CalendarRows ──────────────────────────────────────────────
interface CalendarRowsProps {
  day: Date;
  dateStr: string;
  dayReports: Report[];
  idx: number;
  today: boolean;
  weekend: boolean;
  isEmpty: boolean;
  hasReports: boolean;
  isExpanded: boolean;
  canReport: boolean;
  isMobile: boolean;
  columnCount: number;
  lang: string;
  t: (key: string, opts?: any) => string;
  todayRef?: React.Ref<HTMLTableRowElement>;
  onEmptyClick: (dateStr: string) => void;
  onToggle: (dateStr: string) => void;
  onSubRowClick: (report: Report) => void;
}

function CalendarRows({
  day, dateStr, dayReports, idx: _idx, today, weekend, isEmpty, hasReports,
  isExpanded, canReport, isMobile, columnCount, lang, t, todayRef,
  onEmptyClick, onToggle, onSubRowClick,
}: CalendarRowsProps) {
  return (
    <>
      {/* Main row */}
      <tr
        ref={todayRef}
        className={`transition-colors border-b ${
          today ? 'bg-sky-50/50' : ''
        } ${isEmpty && !canReport ? '' : 'cursor-pointer hover:bg-white/20'}`}
        style={{ borderColor: '#CED0D4' }}
        onClick={() => {
          if (isEmpty && canReport) onEmptyClick(dateStr);
          else if (hasReports) onToggle(dateStr);
        }}
      >
        {/* Date Column */}
        <td className="ps-6 pe-4 py-1.5 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            {today && (
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: C.primary }}
              />
            )}
            <span
              className={`text-sm ${today ? 'font-semibold' : 'font-medium'} ${weekend ? 'text-slate-400' : 'text-neutral-800'}`}
              style={today ? { color: C.primary } : undefined}
            >
              {formatDayLabel(day, lang)}
            </span>
          </div>
        </td>

        {/* Reports Column */}
        <td className="px-4 py-1.5 text-center whitespace-nowrap">
          {hasReports && (() => {
            const byStatus: Record<string, number> = {};
            for (const r of dayReports) {
              const key = r.status === 'SUBMITTED' && r.rejectionReason ? 'RESUBMITTED' : r.status;
              byStatus[key] = (byStatus[key] || 0) + 1;
            }
            return (
              <div className="inline-flex items-center gap-1.5 flex-wrap justify-center">
                {Object.entries(byStatus).map(([status, count]) => {
                  const cfgKey = status === 'RESUBMITTED' ? 'SUBMITTED' : status;
                  const cfg = STATUS_CONFIG[cfgKey] || STATUS_CONFIG.DRAFT;
                  return (
                    <span
                      key={status}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: cfg.bg, color: cfg.color }}
                    >
                      <Copy size={11} strokeWidth={2} />
                      {count} {t(`status.${status.toLowerCase()}`)}
                    </span>
                  );
                })}
              </div>
            );
          })()}
        </td>

        {/* Action Column */}
        <td className="ps-4 pe-6 py-1.5 text-end whitespace-nowrap">
          {isEmpty ? (
            canReport ? (
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-md"
                style={{ backgroundColor: C.primarySoft }}
              >
                <Plus size={12} strokeWidth={3} style={{ color: C.primary }} />
              </span>
            ) : null
          ) : (
            <span
              className="inline-flex items-center justify-center w-6 h-6 rounded-md transition-colors"
              style={{ backgroundColor: isExpanded ? C.primarySoft : 'transparent' }}
            >
              <ChevronDown
                className="w-3.5 h-3.5 transition-transform"
                style={{
                  color: isExpanded ? C.primary : C.textTertiary,
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </span>
          )}
        </td>
      </tr>

      {/* Expanded sub-rows */}
      {hasReports && isExpanded && dayReports.map((r) => {
        const statusCfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.DRAFT;
        return (
          <tr key={r.id}>
            <td colSpan={columnCount}>
              <div
                className="mx-3 my-1 px-3 py-2.5 rounded-lg cursor-pointer transition-all hover:shadow-sm"
                style={{
                  backgroundColor: '#F0F2F5',
                  border: '1px solid #CED0D4',
                }}
                onClick={() => onSubRowClick(r)}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    {r.reportTypeCode === 'TREATMENT' ? (
                      <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: C.primarySoft }}>
                        <Stethoscope size={12} strokeWidth={2} style={{ color: C.primary }} />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: C.leafSoft }}>
                        <BookOpen size={12} strokeWidth={2} style={{ color: C.leaf }} />
                      </div>
                    )}
                    <span className="text-xs font-medium truncate" style={{ color: C.textPrimary }}>
                      {r.hospital ? localizedName(r.hospital, lang) : '-'}
                    </span>
                    <span className="text-xs" style={{ color: C.textTertiary }}>
                      {r.startTime}-{r.endTime}
                    </span>
                    <span className="text-xs hidden sm:inline" style={{ color: C.textSecondary }}>
                      {r.reportTypeCode === 'TREATMENT'
                        ? (r.treatmentType ? t(`treatments.${r.treatmentType}`, r.treatmentType) : '')
                        : (r.description || t('reports.teaching'))}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isMobile ? (
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: statusCfg.dot }}
                      />
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
                        style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}
                      >
                        {statusCfg.icon}
                        {t(`status.${r.status.toLowerCase()}`)}
                      </span>
                    )}
                    <span className="text-sm font-medium" style={{ color: C.textPrimary }}>
                      {`\u00D7${r.quantity || 1}`}
                    </span>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}

// ─── Detail Row helper ─────────────────────────────────────────
function DetailRow({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center py-0.5">
      <span className="flex items-center gap-2 text-sm" style={{ color: C.textTertiary }}>
        {icon}
        {label}
      </span>
      <span className="text-sm font-medium" style={{ color: C.textPrimary }}>
        {children}
      </span>
    </div>
  );
}
